/**
 * ItemDatadogSignalsPanel
 *
 * Fetches Datadog reliability signals (SLO status, incident counts, monitor
 * alerting) for the workspace and lets the user attach a snapshot as
 * first-class Evidence on this item. Import / read-only.
 *
 * Rendered in the Evidence tab of ItemDetailPage.
 */

import { useState, useEffect, useCallback } from 'react';
import {
  Activity,
  AlertCircle,
  Check,
  ChevronDown,
  ChevronUp,
  Loader2,
  Paperclip,
  RefreshCcw,
  ShieldCheck,
  ShieldAlert,
} from 'lucide-react';
import {
  getIntegrationsApi,
  type DatadogReliabilitySignals,
} from '../../../lib/integrationsApi';
import { getCurrentWorkspaceId } from '../../../lib/supabaseSync';
import { useEvidenceStore } from '../../../stores/prioritization/evidenceStore';

// ── Datadog icon ────────────────────────────────────────────────────────────

const DatadogIcon = ({ className = 'w-4 h-4' }: { className?: string }) => (
  <svg viewBox="0 0 24 24" className={className} fill="none">
    <rect width="24" height="24" rx="4" fill="#632CA6" />
    <path
      d="M5 16L8 8L11 14L14 10L17 12L19 8"
      stroke="white"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      fill="none"
    />
    <circle cx="8" cy="8" r="1.2" fill="#FFCC00" />
    <circle cx="14" cy="10" r="1.2" fill="#FFCC00" />
    <circle cx="19" cy="8" r="1.2" fill="#FFCC00" />
  </svg>
);

interface ItemDatadogSignalsPanelProps {
  itemId: string;
  itemTitle?: string;
}

function formatSli(value: number | null): string {
  if (value === null || Number.isNaN(value)) return '—';
  return `${value.toFixed(2)}%`;
}

/** Build a compact human-readable evidence summary from the signals. */
function buildSummary(s: DatadogReliabilitySignals): string {
  const breaching = s.slos.filter((slo) => slo.meetingTarget === false).length;
  const met = s.slos.filter((slo) => slo.meetingTarget === true).length;
  const sloPart =
    s.slos.length > 0
      ? `SLOs ${met} meeting / ${breaching} breaching`
      : 'no SLOs configured';
  return `Datadog reliability snapshot — ${sloPart}; ${s.incidents.active} active / ${s.incidents.total} total incidents; ${s.monitors.alerting} alerting, ${s.monitors.warning} warning monitors.`;
}

export function ItemDatadogSignalsPanel({ itemId, itemTitle }: ItemDatadogSignalsPanelProps) {
  const workspaceId = getCurrentWorkspaceId();
  const addEvidence = useEvidenceStore((s) => s.addEvidence);

  const [isOpen, setIsOpen] = useState(false);
  const [connected, setConnected] = useState<boolean | null>(null);
  const [loading, setLoading] = useState(false);
  const [signals, setSignals] = useState<DatadogReliabilitySignals | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [attached, setAttached] = useState(false);

  const load = useCallback(async () => {
    if (!workspaceId) return;
    setLoading(true);
    setError(null);
    try {
      const status = await getIntegrationsApi(workspaceId).getDatadogStatus();
      setConnected(status.connected);
      if (!status.connected) {
        setSignals(null);
        return;
      }
      const data = await getIntegrationsApi(workspaceId).getDatadogReliabilitySignals({
        sloQuery: itemTitle || undefined,
        sloLimit: 10,
      });
      setSignals(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load Datadog signals');
      setConnected((prev) => (prev === null ? false : prev));
    } finally {
      setLoading(false);
    }
  }, [workspaceId, itemTitle]);

  // Fetch when the panel is first opened.
  useEffect(() => {
    if (isOpen && connected === null && !loading) void load();
  }, [isOpen, connected, loading, load]);

  const handleAttach = () => {
    if (!signals) return;
    const breaching = signals.slos.filter((s) => s.meetingTarget === false);
    addEvidence({
      itemId,
      type: 'data',
      // Datadog is the authoritative source for a workspace's own reliability data.
      reliability: 'verified',
      accessLevel: 'internal',
      source: 'Datadog',
      sourceType: 'observability',
      supportsClaim: 'Current reliability posture (SLOs, incidents, monitor alerting)',
      summary: buildSummary(signals),
      rawContent: JSON.stringify(
        {
          fetchedAt: signals.fetchedAt,
          slos: signals.slos,
          incidents: signals.incidents,
          monitors: signals.monitors,
        },
        null,
        2,
      ),
      tags: ['datadog', 'reliability', ...(breaching.length > 0 ? ['slo-breach'] : [])],
    });
    setAttached(true);
    setTimeout(() => setAttached(false), 1800);
  };

  return (
    <div className="pt-3 border-t border-[var(--rule)]">
      {/* Section header */}
      <div className="flex items-center justify-between mb-2">
        <p className="text-xs font-semibold text-[var(--text-ter)] dark:text-[var(--text-ter)] uppercase tracking-wide flex items-center gap-1.5">
          <DatadogIcon className="w-3.5 h-3.5" />
          Datadog reliability signals
        </p>
        <button
          onClick={() => setIsOpen((v) => !v)}
          className="flex items-center gap-1 text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)] hover:text-[var(--text-ter)] dark:hover:text-[var(--text-ter)] transition-colors"
        >
          {isOpen ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
          {isOpen ? 'Hide' : 'Show signals'}
        </button>
      </div>

      {!isOpen && (
        <p className="text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)] italic py-0.5">
          Pull SLO status, incidents, and monitor alerting as evidence.
        </p>
      )}

      {isOpen && (
        <div className="mt-1 border border-[#632CA6]/25 dark:border-[#632CA6]/40 bg-[var(--bg-raised)] dark:bg-[var(--surface)] shadow-sm overflow-hidden">
          <div className="p-3 space-y-3">
            {loading && (
              <div className="flex items-center gap-1.5 text-xs text-[var(--text-ter)] py-2">
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                Loading reliability signals…
              </div>
            )}

            {/* Not connected */}
            {!loading && connected === false && (
              <div className="flex items-start gap-2 p-2.5 bg-[var(--mustard-dim)] dark:bg-[var(--mustard)]/20 border border-[var(--mustard)] dark:border-[var(--mustard)]">
                <AlertCircle className="h-3.5 w-3.5 text-[var(--mustard)] flex-shrink-0 mt-0.5" />
                <p className="text-xs text-[var(--mustard)] dark:text-[var(--mustard)]">
                  Datadog is not connected. Go to{' '}
                  <a href="/prioritization/settings/connectors" className="underline font-medium">
                    Settings → Connectors
                  </a>{' '}
                  to connect your Datadog account.
                </p>
              </div>
            )}

            {/* Error */}
            {!loading && error && connected !== false && (
              <div className="flex items-start gap-1.5 text-xs text-[var(--coral)] dark:text-[var(--coral)] p-2 bg-[var(--coral-dim)] dark:bg-[var(--coral)]/20">
                <AlertCircle className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" />
                {error}
              </div>
            )}

            {/* Signals */}
            {!loading && signals && connected && (
              <>
                {/* Counts row */}
                <div className="grid grid-cols-3 gap-2">
                  <div className="border border-[var(--rule)] bg-[var(--surface)] dark:bg-[var(--bg-raised)]/40 px-2.5 py-2 text-center">
                    <p className="text-lg font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)] leading-none">
                      {signals.incidents.active}
                    </p>
                    <p className="text-[10px] text-[var(--text-ter)] dark:text-[var(--text-ter)] mt-1 uppercase tracking-wide">
                      Active incidents
                    </p>
                  </div>
                  <div className="border border-[var(--rule)] bg-[var(--surface)] dark:bg-[var(--bg-raised)]/40 px-2.5 py-2 text-center">
                    <p className="text-lg font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)] leading-none">
                      {signals.monitors.alerting}
                    </p>
                    <p className="text-[10px] text-[var(--text-ter)] dark:text-[var(--text-ter)] mt-1 uppercase tracking-wide">
                      Alerting monitors
                    </p>
                  </div>
                  <div className="border border-[var(--rule)] bg-[var(--surface)] dark:bg-[var(--bg-raised)]/40 px-2.5 py-2 text-center">
                    <p className="text-lg font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)] leading-none">
                      {signals.monitors.warning}
                    </p>
                    <p className="text-[10px] text-[var(--text-ter)] dark:text-[var(--text-ter)] mt-1 uppercase tracking-wide">
                      Warning monitors
                    </p>
                  </div>
                </div>

                {/* SLO list */}
                {signals.slos.length > 0 ? (
                  <div className="space-y-1">
                    <p className="text-[10px] text-[var(--text-ter)] dark:text-[var(--text-ter)] uppercase tracking-wide">
                      SLOs
                    </p>
                    {signals.slos.map((slo) => (
                      <div
                        key={slo.id}
                        className="flex items-center gap-2 px-2 py-1.5 bg-[var(--surface)] dark:bg-[var(--bg-raised)]/40 border border-[var(--rule)]"
                      >
                        {slo.meetingTarget === false ? (
                          <ShieldAlert className="h-3.5 w-3.5 text-[var(--coral)] flex-shrink-0" />
                        ) : slo.meetingTarget === true ? (
                          <ShieldCheck className="h-3.5 w-3.5 text-[var(--green)] flex-shrink-0" />
                        ) : (
                          <Activity className="h-3.5 w-3.5 text-[var(--text-ter)] flex-shrink-0" />
                        )}
                        <span className="text-xs text-[var(--text-sec)] dark:text-[var(--text-sec)] truncate flex-1">
                          {slo.name}
                        </span>
                        <span
                          className={`text-xs font-mono font-medium ${
                            slo.meetingTarget === false
                              ? 'text-[var(--coral)] dark:text-[var(--coral)]'
                              : 'text-[var(--text-sec)] dark:text-[var(--text-sec)]'
                          }`}
                          title={`Target ${slo.target}% over ${slo.timeframe}`}
                        >
                          {formatSli(slo.sliValue)} / {slo.target}%
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-xs text-[var(--text-ter)] italic">No SLOs configured in Datadog.</p>
                )}

                {/* Actions */}
                <div className="flex items-center justify-between pt-1">
                  <button
                    type="button"
                    onClick={() => void load()}
                    className="flex items-center gap-1 text-xs text-[var(--text-ter)] dark:text-[var(--text-sec)] hover:text-[var(--text-pri)] dark:hover:text-[var(--text-pri)] transition-colors"
                  >
                    <RefreshCcw className="h-3 w-3" />
                    Refresh
                  </button>
                  <button
                    type="button"
                    onClick={handleAttach}
                    disabled={attached}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium transition-colors disabled:opacity-60 bg-[var(--slate)] hover:bg-[var(--slate)] text-white"
                  >
                    {attached ? (
                      <>
                        <Check className="h-3 w-3" />
                        Attached
                      </>
                    ) : (
                      <>
                        <Paperclip className="h-3 w-3" />
                        Attach as evidence
                      </>
                    )}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
